import type { CollapsibleContentProps } from "../types";

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Tooltip } from "@heroui/react";

import {
  DEFAULT_MAX_CONTENT_LENGTH,
  DEFAULT_EXPANDED_HEIGHT,
  MIN_EXPANDED_HEIGHT,
  MAX_EXPANDED_HEIGHT,
} from "../constants";

export const CollapsibleContent: React.FC<CollapsibleContentProps> = ({
  content,
  maxLength = DEFAULT_MAX_CONTENT_LENGTH,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [expandedHeight, setExpandedHeight] = useState(DEFAULT_EXPANDED_HEIGHT);

  const shouldShowToggle = content.length > maxLength;
  const displayContent = isExpanded ? content : content.slice(0, maxLength) + "...";

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStartY(e.clientY);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging && isExpanded) {
      const deltaY = e.clientY - dragStartY;
      const newHeight = Math.max(
        MIN_EXPANDED_HEIGHT,
        Math.min(MAX_EXPANDED_HEIGHT, expandedHeight + deltaY),
      );

      setExpandedHeight(newHeight);
      setDragStartY(e.clientY);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove as any);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove as any);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragStartY, expandedHeight]);

  return (
    <div
      className={`transition-all duration-200 ease-in-out relative ${
        isExpanded ? "overflow-y-auto" : "overflow-hidden"
      }`}
      style={isExpanded ? { maxHeight: `${expandedHeight}px` } : {}}
    >
      <span className="whitespace-pre-wrap break-words block">{displayContent}</span>
      {shouldShowToggle && (
        <div
          className="flex items-center gap-2 w-full bg-gradient-to-t from-white/90 to-transparent dark:from-gray-900/90 dark:to-transparent"
          style={{
            position: "sticky",
            bottom: 0,
            left: 0,
            zIndex: 20,
            padding: "0.25rem 0.5rem",
            justifyContent: "flex-end",
            minHeight: "2.5rem",
          }}
        >
          <Button
            className="text-brand-primary text-xs p-1 min-w-0 h-6 bg-transparent hover:bg-gray-100/50 dark:hover:bg-gray-700/30 border-none shadow-none"
            size="sm"
            variant="light"
            onPress={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? t("See less") : t("See more")}
          </Button>
          {isExpanded && (
            <Tooltip content={t("Drag to resize")}>
              <div
                className="w-4 h-4 bg-gray-300 rounded-full cursor-ns-resize hover:bg-gray-400 transition-colors"
                title="Drag to resize"
                onMouseDown={handleMouseDown}
              />
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};
